import Assignment from './Assignment';
import AssignmentSubmission from './AssignmentSubmission';
import StudentProfile from './StudentProfile';

export default class TeacherGradebookEntry {
  public username: string;
  public first_name: string;
  public last_name: string;
  public email: string;
  public student_profile: StudentProfile;
  public submissions: AssignmentSubmission[];
  public averages: Record<string, number>;
  public reading_group: number;
  public user_id: string;

  constructor(
    username: string,
    first_name: string,
    last_name: string,
    email: string,
    student_profile: StudentProfile,
    submissions: AssignmentSubmission[],
    averages: Record<string, number>,
    reading_group: number,
    user_id: string,
  ) {
    this.username = username;
    this.first_name = first_name;
    this.last_name = last_name;
    this.email = email;
    this.student_profile = student_profile;
    this.submissions = submissions;
    this.averages = averages;
    this.reading_group = reading_group;
    this.user_id = user_id;
  }

  static fromServerEntry(
    serverEntry: Record<string, any>,
    assignments: Assignment[],
  ): TeacherGradebookEntry | null {
    try {
      const submissions = serverEntry.submissions
        .filter(
          (serverAssignmentSubmission: Record<string, any>) =>
            assignments.find(
              (asgn) =>
                Number(serverAssignmentSubmission.assignment) ===
                Number(asgn.id),
            ) !== undefined,
        )
        .map((serverAssignmentSubmission: Record<string, any>) =>
          AssignmentSubmission.fromServerAssignmentSubmission(
            serverAssignmentSubmission,
          ),
        )
        .sort((a1: AssignmentSubmission, a2: AssignmentSubmission) => {
          const completed1 = a1.date_completed
            ? new Date(a1.date_completed)
            : null;
          const completed2 = a2.date_completed
            ? new Date(a2.date_completed)
            : null;

          // Sort by descending date_completed
          if (completed1 && completed2) {
            const diff = completed2.getTime() - completed1.getTime();
            if (diff !== 0) return diff;
          } else if (completed1 && !completed2) {
            return -1; // completed1 comes first
          } else if (!completed1 && completed2) {
            return 1; // completed2 comes first
          }

          // If both are uncompleted or completed on the same date, sort by due date
          const assignment1 = assignments.find(
            (asgn) => Number(asgn.id) === Number(a1.assignment),
          );
          const assignment2 = assignments.find(
            (asgn) => Number(asgn.id) === Number(a2.assignment),
          );

          const due1 = assignment1?.due_date
            ? new Date(assignment1.due_date)
            : null;
          const due2 = assignment2?.due_date
            ? new Date(assignment2.due_date)
            : null;

          if (due1 && due2) {
            return due2.getTime() - due1.getTime(); // later due date first
          } else if (due1 && !due2) {
            return -1; // due1 comes first
          } else if (!due1 && due2) {
            return 1; // due2 comes first
          }

          // If neither has due_date or date_completed, keep their order
          return 0;
        });
      const student_profile = StudentProfile.fromServerStudentProfile(
        serverEntry.student_profile,
      );
      return new TeacherGradebookEntry(
        serverEntry.username,
        serverEntry.first_name,
        serverEntry.last_name,
        serverEntry.email,
        student_profile,
        submissions,
        serverEntry.averages,
        serverEntry.reading_group,
        serverEntry.user_id,
      );
    } catch (error) {
      console.error(error);
      console.error(
        `Failed to load server entry: ${JSON.stringify(serverEntry)}`,
      );
      return null;
    }
  }

  displayName() {
    return `${this.first_name} ${this.last_name}`;
  }
}
